import React, { useEffect, useState, useRef } from 'react';

const ResponsiveWrapper = ({ children }) => {
  const [containerSize, setContainerSize] = useState({
    width: window.innerWidth,
    height: window.innerHeight,
    isEmbedded: window.self !== window.top,
  });

  const containerRef = useRef(null);

  useEffect(() => {
    const updateSize = () => {
      if (containerRef.current) {
        const rect = containerRef.current.getBoundingClientRect();
        setContainerSize({
          width: rect.width,
          height: rect.height,
          isEmbedded: window.self !== window.top,
        });
      }
    };

    // Initial size update
    updateSize();

    // Create ResizeObserver to watch container size changes
    const resizeObserver = new ResizeObserver(() => {
      updateSize();
    });

    if (containerRef.current) {
      resizeObserver.observe(containerRef.current);
    }

    // Fallback for window resize events
    window.addEventListener('resize', updateSize);

    // Listen for messages from parent iframe (if embedded)
    const handleMessage = (event) => {
      if (event.data?.type === 'IFRAME_RESIZE') {
        updateSize();
      }
    };
    window.addEventListener('message', handleMessage);

    return () => {
      resizeObserver.disconnect();
      window.removeEventListener('resize', updateSize);
      window.removeEventListener('message', handleMessage);
    };
  }, []);

  // Update CSS custom properties whenever container size changes
  useEffect(() => {
    // Calculate scale factor for severely constrained containers
    const scaleFactor = containerSize.isEmbedded
      ? Math.min(containerSize.width / 1920, containerSize.height / 1080, 1)
      : 1;

    // Set CSS custom properties
    document.documentElement.style.setProperty('--container-width', `${containerSize.width}px`);
    document.documentElement.style.setProperty('--container-height', `${containerSize.height}px`);
    document.documentElement.style.setProperty('--scale-factor', scaleFactor.toString());
    document.documentElement.style.setProperty('--is-embedded', containerSize.isEmbedded ? '1' : '0');

    // Add body class for embedded mode
    if (containerSize.isEmbedded) {
      document.body.classList.add('embedded-mode');
    } else {
      document.body.classList.remove('embedded-mode');
    }

  }, [containerSize]);

  return (
    <div
      ref={containerRef}
      className="w-full min-h-screen relative"
    >
      {children}
    </div>
  );
};

export default ResponsiveWrapper;